import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  MenuItem,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import MDBox from "components/MDBox";
import { createProcess, updateProcess } from "app/services/iqms.service";

const PROCESS_STATUSES = ["DRAFT", "ACTIVE", "UNDER_REVIEW", "DEPRECATED"];
const PROCESS_CATEGORIES = ["DEVELOPMENT", "TESTING", "DEPLOYMENT", "MAINTENANCE", "SUPPORT", "MANAGEMENT"];

const initialData = {
  processId: null,
  processCode: "",
  processName: "",
  description: "",
  category: "DEVELOPMENT",
  ownerId: "",
  reviewerId: "",
  status: "DRAFT",
  version: "1.0",
  createdBy: 1, // Default to admin user
};

const AddProcessDialog = ({ open, setOpen, onSuccess, editObject }) => {
  const [data, setData] = useState(initialData);
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editObject) {
      setData({
        processId: editObject.processId || editObject.id || null,
        processCode: editObject.processCode || "",
        processName: editObject.processName || "",
        description: editObject.description || "",
        category: editObject.category || "DEVELOPMENT",
        ownerId: editObject.ownerId || "",
        reviewerId: editObject.reviewerId || "",
        status: editObject.status || "DRAFT",
        version: editObject.version || "1.0",
        createdBy: editObject.createdBy || 1,
      });
    } else {
      setData(initialData);
    }
    setMessageOpen(false);
  }, [editObject, open]);

  const validateForm = () => {
    if (!data.processCode || data.processCode.trim() === "") {
      setError("Process code is required");
      return false;
    }
    if (!data.processName || data.processName.trim() === "") {
      setError("Process name is required");
      return false;
    }
    if (!data.ownerId) {
      setError("Owner ID is required");
      return false;
    }
    return true;
  };

  const setError = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccess = (successMessage) => {
    setMessage(successMessage);
    setMessageOpen(true);
    setSeverity("success");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Transform data to match backend entity requirements
      const submitData = {
        ...data,
        processId: data.processId || null,
        ownerId: data.ownerId ? parseInt(data.ownerId, 10) : 1,
        reviewerId: data.reviewerId ? parseInt(data.reviewerId, 10) : null,
        createdBy: data.createdBy ? parseInt(data.createdBy, 10) : 1,
      };

      if (editObject) {
        await updateProcess(data.processId, submitData);
        setSuccess("Process updated successfully");
      } else {
        await createProcess(submitData);
        setSuccess("Process created successfully");
      }
      setTimeout(() => {
        setData(initialData);
        setMessageOpen(false);
        setOpen(false);
        onSuccess && onSuccess();
      }, 1000);
    } catch (error) {
      setError(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setData(initialData);
    setMessageOpen(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editObject ? "Edit Process" : "Add New Process"}</DialogTitle>
      <DialogContent>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="processCode"
            label="Process Code"
            fullWidth
            variant="outlined"
            value={data.processCode}
            onChange={(e) => setData({ ...data, processCode: e.target.value })}
            required
          />
          <TextField
            id="version"
            label="Version"
            variant="outlined"
            value={data.version}
            onChange={(e) => setData({ ...data, version: e.target.value })}
            sx={{ width: 120 }}
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="processName"
            label="Process Name"
            fullWidth
            variant="outlined"
            value={data.processName}
            onChange={(e) => setData({ ...data, processName: e.target.value })}
            required
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="category"
            label="Category"
            fullWidth
            select
            variant="outlined"
            value={data.category}
            onChange={(e) => setData({ ...data, category: e.target.value })}
          >
            {PROCESS_CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </TextField>
          <TextField
            id="status"
            label="Status"
            fullWidth
            select
            variant="outlined"
            value={data.status}
            onChange={(e) => setData({ ...data, status: e.target.value })}
          >
            {PROCESS_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>{status.replace("_", " ")}</MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="ownerId"
            label="Owner ID"
            fullWidth
            variant="outlined"
            type="number"
            value={data.ownerId}
            onChange={(e) => setData({ ...data, ownerId: e.target.value })}
            helperText="Enter member ID of the process owner"
            required
          />
          <TextField
            id="reviewerId"
            label="Reviewer ID"
            fullWidth
            variant="outlined"
            type="number"
            value={data.reviewerId}
            onChange={(e) => setData({ ...data, reviewerId: e.target.value })}
            helperText="Optional: Enter member ID of reviewer"
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="description"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={data.description}
            onChange={(e) => setData({ ...data, description: e.target.value })}
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            severity={severity}
            action={
              <IconButton size="small" onClick={() => setMessageOpen(false)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} disabled={isSubmitting} variant="contained">
          {editObject ? "Update" : "Add"}
        </Button>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddProcessDialog);

